/**
 * RAM — Change account features (Wave 10).
 *
 * POST /v3/maint/acct/change-features
 *
 * Replaces the Wave10ActionFallback for `change-features`. Renders all active
 * Features grouped by `categoryCode`; each row exposes three radios:
 *   - add        → feature flagged into `featuresAdded`
 *   - remove     → feature flagged into `featuresRemoved`
 *   - no change  → feature omitted from both arrays (default)
 *
 * The two-list "available vs current" UX described in the design doc requires
 * us to know the account's currently-attached features. That readback isn't
 * wired yet (no `/v3/account/{id}/features` route), so v1 ships this single
 * grouped list with per-row tri-state. Once the readback lands we flip to the
 * two-list selector without churning the submit body.
 *
 * Some toggles (eStatement, debitCard) require ancillary signatures — surfaced
 * via the info banner; the actual ancillary-doc gating happens server-side
 * (FeatureSignatureRules). Approval routing per
 * MaintActionRoutingRules → admin-config.
 */
import { useState } from 'react';
import { Banner, Button, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useConfig } from '@/contexts/ConfigContext';
import { useMaintActionSubmit } from '@/modules/Maint/useMaintActionSubmit';
import type { RamTarget } from '../types';

export interface ChangeFeaturesProps {
  target: RamTarget;
  onDone: () => void;
  onCancel: () => void;
}

type RowChoice = 'none' | 'add' | 'remove';

export default function ChangeFeatures({
  target,
  onDone,
  onCancel,
}: ChangeFeaturesProps): JSX.Element {
  const client = useApiClient();
  const { config } = useConfig();
  const { submit, submitting, error } = useMaintActionSubmit();

  const [choices, setChoices] = useState<Record<string, RowChoice>>({});
  const [reason, setReason] = useState<string>('');
  const [done, setDone] = useState<boolean>(false);
  const [approvalNote, setApprovalNote] = useState<string | null>(null);

  const features = (config?.features ?? []).filter((f) => f.isActive);

  // Group by categoryCode for the UI. Map iteration preserves insertion order.
  const grouped = new Map<string, Array<typeof features[number]>>();
  for (const f of features) {
    const key = f.categoryCode || 'OTHER';
    const bucket = grouped.get(key);
    if (bucket) bucket.push(f);
    else grouped.set(key, [f]);
  }

  const featuresAdded = Object.entries(choices)
    .filter(([, v]) => v === 'add')
    .map(([k]) => k);
  const featuresRemoved = Object.entries(choices)
    .filter(([, v]) => v === 'remove')
    .map(([k]) => k);

  const hasChange = featuresAdded.length > 0 || featuresRemoved.length > 0;
  const canSave = !submitting && hasChange && Boolean(target.acctId);

  const setChoice = (code: string, choice: RowChoice): void => {
    setChoices((prev) => ({ ...prev, [code]: choice }));
  };

  const handleSave = async (): Promise<void> => {
    if (!canSave || !target.acctId) return;
    const result = await submit(
      (b) => client.retail.maintActions.submitChangeFeatures(b),
      {
        accountNumber: target.acctId,
        featuresAdded: featuresAdded.length > 0 ? featuresAdded : undefined,
        featuresRemoved: featuresRemoved.length > 0 ? featuresRemoved : undefined,
        reason: reason.trim() || undefined,
      },
    );
    if (!result.success) return;
    if (result.data?.approvalRequired) {
      setApprovalNote(
        `Submitted — awaiting ${result.data.approverRole ?? 'approver'} approval (scenario: ${result.data.approvalScenario ?? 'default'}).`,
      );
    } else if (result.data?.testModeSkipped) {
      setApprovalNote('Test-mode — jXchange call skipped. Audit row recorded.');
    } else {
      setApprovalNote(null);
    }
    setDone(true);
  };

  if (done) {
    return (
      <div className="ram-wizard">
        <Banner tone="ok">
          Feature change submitted for account {target.acctId}.
        </Banner>
        {approvalNote && <Banner tone="info">{approvalNote}</Banner>}
        <div className="ram-wizard__actions">
          <Button variant="primary" onClick={onDone}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="ram-wizard">
      <div className="ram-wizard__header">
        <h2 className="ram-wizard__title">Add / remove account features</h2>
        <Pill variant="info">CIF {target.cifId}</Pill>
        {target.acctId && <Pill variant="muted">Account {target.acctId}</Pill>}
      </div>

      <Banner tone="info">
        Feature toggles take effect on next statement cycle. Some features
        (eStatement, debitCard) require ancillary signatures.
      </Banner>

      {features.length === 0 ? (
        <Banner tone="warn">
          No active features in config — admin must seed the Features catalog
          before this action is usable.
        </Banner>
      ) : (
        <div className="ram-wizard__fields">
          {Array.from(grouped.entries()).map(([categoryCode, rows]) => (
            <fieldset key={categoryCode} className="ram-fieldset">
              <legend className="ram-fieldset__legend">{categoryCode}</legend>
              {rows.map((f) => {
                const choice = choices[f.code] ?? 'none';
                const groupName = `feat-${f.code}`;
                return (
                  <div key={f.code} className="ram-feature-row">
                    <div className="ram-feature-row__label">
                      <strong>{f.displayName}</strong>
                      <span className="ram-feature-row__code"> ({f.code})</span>
                    </div>
                    <div className="ram-feature-row__choices">
                      <label>
                        <input
                          type="radio"
                          name={groupName}
                          checked={choice === 'add'}
                          onChange={() => setChoice(f.code, 'add')}
                        />{' '}
                        Add
                      </label>
                      <label>
                        <input
                          type="radio"
                          name={groupName}
                          checked={choice === 'remove'}
                          onChange={() => setChoice(f.code, 'remove')}
                        />{' '}
                        Remove
                      </label>
                      <label>
                        <input
                          type="radio"
                          name={groupName}
                          checked={choice === 'none'}
                          onChange={() => setChoice(f.code, 'none')}
                        />{' '}
                        No change
                      </label>
                    </div>
                  </div>
                );
              })}
            </fieldset>
          ))}

          <label className="ram-search__label">
            Reason
            <textarea
              className="ram-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={512}
            />
          </label>
        </div>
      )}

      {!hasChange && features.length > 0 && (
        <Banner tone="info">
          Mark at least one feature to add or remove.
        </Banner>
      )}

      {error && <Banner tone="err">{error}</Banner>}

      <div className="ram-wizard__actions">
        <Button variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          {submitting ? 'Submitting…' : 'Submit feature change'}
        </Button>
      </div>
    </div>
  );
}
